import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Debouncing for API calls to prevent spam
const debounceMap = new Map();
const DEBOUNCE_TIME = 5000; // 5 seconds

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);

  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }

  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetReportsCaches = () => {
  debounceMap.clear();
};

const buildDebounceKey = ({ startTime, endTime, memberIds, responseTimeMin, responseTimeMax, resolutionTimeMin, resolutionTimeMax }) =>
  `fetchReports_${startTime}_${endTime}_${JSON.stringify(memberIds) || ""}_${responseTimeMin ?? ""}_${responseTimeMax ?? ""}_${resolutionTimeMin ?? ""}_${resolutionTimeMax ?? ""}`;

export const fetchReports = createAsyncThunk(
  "reports/fetchReports",
  async (
    { startTime, endTime, memberIds, responseTimeMin, responseTimeMax, resolutionTimeMin, resolutionTimeMax },
    { rejectWithValue }
  ) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      // Fetch data from the combined reports route. Filter fields are only
      // included when actually set (undefined keys are dropped by
      // JSON.stringify) -- an absent filter means "don't filter on this",
      // matching the backend validator's "all optional" contract.
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teammembers/reports`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            startTime,
            endTime,
            memberIds,
            responseTimeMin,
            responseTimeMax,
            resolutionTimeMin,
            resolutionTimeMax,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || "Failed to fetch combined reports");
      }

      const combinedReports = await response.json();
      return combinedReports;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
  {
    // Debounce as a genuine skip (2026-08-15 fix) -- previously this check
    // lived inside the payload creator, which meant a debounced call still
    // dispatched `pending` (and therefore still occupied "the latest
    // request" in the race-guard below) before rejecting with "Debounced".
    // That would have orphaned a real in-flight earlier request's response
    // once the race guard was added. `condition` is RTK's built-in tool for
    // "don't dispatch this thunk at all" -- a debounced call now produces
    // no pending/fulfilled/rejected action whatsoever, so it can never
    // interfere with tracking the genuinely-latest request.
    condition: (arg) => !shouldDebounce(buildDebounceKey(arg)),
  }
);

const reportsSlice = createSlice({
  name: "reports",
  initialState: {
    reports: [],
    loading: false,
    error: null,
    // Tracks the most recently DISPATCHED request's id -- lets fulfilled/
    // rejected below detect and ignore a stale, out-of-order response (a
    // real race: e.g. a fast "this week" response landing after a slower
    // "custom range" response that was dispatched later but resolved
    // first, which would otherwise silently show wrong data on screen).
    currentRequestId: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        // A newer request's `pending` has since overwritten currentRequestId
        // -- this response belongs to a stale, superseded request. Ignore it.
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.reports = action.payload;
        state.error = null;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.reports = [];
        state.error = action.payload;
      });
  },
});

export default reportsSlice.reducer;
